import { IApiFilter, IDateRange } from '@fareye/types'
import {
  Card,
  DatePicker,
  Flex,
  Table,
  IRenderControl,
  RenderControl,
  Span,
  Button
} from '@fareye/ui'
import {
  cancelToken,
  get,
  getQueryParams,
  subDays,
  yyyyMmDd
} from '@fareye/utils'
import React from 'react'
import {
  useTimeZone,
  zonedToUTC,
  zonedToUTCAdd1Day,
  useTranslation
} from '@fareye/common'
import {
  IPageConfig,
  TAlertChildrenConfig,
  IColumnConfig
} from '../../../utility/types'
import { HEADER_INFLECTION } from '../../../config/constants'
import { getTableColumns } from '../components/Column'
import { DownloadExcelButton } from './DownloadExcelButton'

interface IAlertReportListing {
  selectedAlert: any
  propConfig: TAlertChildrenConfig
  selectedAlertType: string
  selectedColumns: IColumnConfig[]
  setSelectedAlert(val: any): void
  showTimezone?: boolean
  subType?: string
  reportFilters?: any
  baseUrl: string
}
const initialData = {
  data: null,
  isLoading: false,
  isSuccess: false,
  message: 'Please select above subscription to see the report.',
  isError: false
}

export function AlertReportListing({
  selectedAlert,
  propConfig,
  selectedColumns,
  selectedAlertType,
  setSelectedAlert,
  showTimezone,
  subType,
  reportFilters,
  baseUrl
}: IAlertReportListing) {
  const [alertReportData, setAlertReportData] = React.useState<IRenderControl>(
    initialData
  )
  // state in sync with alertReportData to identify its alertType
  const [reportListingAlertType, setReportListingAlertType] = React.useState(
    selectedAlertType
  )
  const [dateRange, setDateRange] = React.useState({
    startDate: subDays(new Date(), 2),
    endDate: new Date()
  })

  const [filterOptions, setFilterOptions] = React.useState<any>({})
  const [tempFilters, setTempFilters] = React.useState<any>({})    // current selection on UI
  const [appliedFilters, setAppliedFilters] = React.useState<any>({}) // valur for api after Submit

  const { state } = useTimeZone()
  const subscriptionId = selectedAlert ? selectedAlert.id : undefined
  const { startDate, endDate } = dateRange
  const queryParam = React.useMemo(
    () =>
      //NOTE:: Need to refactor xsls function.
      getQueryParams(
        {
          subscription_id: subscriptionId,
          start_date: showTimezone
            ? zonedToUTC(new Date(yyyyMmDd(startDate)))
            : startDate,
          end_date: showTimezone
            ? zonedToUTCAdd1Day(new Date(yyyyMmDd(endDate)))
            : endDate,
          sub_type: subType,
          ...appliedFilters
        },
        showTimezone
      ),
    [dateRange, subscriptionId, subType, appliedFilters, showTimezone]
  )
  const source = cancelToken().source()
  const { t } = useTranslation()

  React.useEffect(() => {
    if (selectedAlert) {
      fetchReportData()
    }
    return () => source.cancel()
  }, [selectedAlert, dateRange, appliedFilters])

  // fetch config form api data
  React.useEffect(() => {
    const fetchOptions = async () => {
      if (reportFilters?.formConfig) {
        const newOptions: any = {}
        const fields: any[] = []
        Object.values(reportFilters.formConfig).forEach((row: any) => {
          row.data.forEach((field: any) => {
            if (field.apiUrl) fields.push(field)
          })
        })

        for (const field of fields) {
          try {
            const response = await get(field.apiUrl, source.token, HEADER_INFLECTION)
            newOptions[field.formKey] = response.data[field.searchQueryKey] || []
          } catch (e) {
            console.error(`Error fetching ${field.formKey}`, e)
          }
        }
        setFilterOptions(newOptions)
      }
    }
    fetchOptions()
  }, [reportFilters])

  React.useEffect(() => {
    setAlertReportData(initialData)
    setSelectedAlert(null)
    setReportListingAlertType(selectedAlertType)
    setTempFilters({})
    setAppliedFilters({})
  }, [selectedAlertType])

  async function fetchReportData() {
    const { apiConfig } = (propConfig['listAlertReport'] as IPageConfig).config
    try {
      setAlertReportData({
        ...alertReportData,
        isLoading: true
      })

      const { apiURL } = apiConfig as IApiFilter
      const {
        data: { data }
      } = await get(`${apiURL}?${queryParam}`, source.token, HEADER_INFLECTION)
      setAlertReportData({
        ...alertReportData,
        data,
        isSuccess: true,
        isLoading: false,
        message: !data?.length
          ? 'No report data found for the above selected alert configuration and specified date range.'
          : alertReportData.message
      })
    } catch (ex:any) {
      const msg = ex.response
        ? ex.response.data
          ? ex.response.data.error
          : 'Something Went Wrong...'
        : ''
      setAlertReportData({
        ...alertReportData,
        isLoading: false,
        isSuccess: false,
        message: msg
      })
    }
  }

const renderConfigFilters = () => {
  if (!reportFilters?.formConfig) return null;
  const elements: any[] = [];

  Object.values(reportFilters.formConfig).forEach((row: any) => {
    row.data.forEach((field: any) => {
      const options = filterOptions[field.formKey] || [];
      elements.push(
        <div key={field.formKey} style={{ marginRight: '10px' }}>
          <select
            style={{
              height: '32px',
              minWidth: '160px',
              borderRadius: '4px',
              border: '1px solid #d9d9d9',
              padding: '0 8px',
              fontSize: '12px',
              backgroundColor: 'white',
              cursor: 'pointer'
            }}
            value={tempFilters[field.formKey] || ""}
            onChange={(e) => {
              const updated = { ...tempFilters, [field.formKey]: e.target.value }
              setTempFilters(updated)
              setAppliedFilters(updated)
            }}
          >
            <option value="">{t(field.label)}</option>
            {options.map((opt: any, idx: number) => {
              const label = opt[field.labelForDisplay]
              const value = opt[field.valueForDisplay]
              return (
                <option key={`${field.formKey}-${idx}`} value={value}>
                  {label}
                </option>
              );
            })}
          </select>
        </div>
      );
    });
  });
  return elements;
};

  function reportCardHeader() {
    const { apiConfig } = (propConfig['downloadReport'] as IPageConfig).config
    const { apiURL } = apiConfig as IApiFilter
    const reportUrl = `${apiURL}?${queryParam}`
    const btnConfig = reportFilters?.btnConfig

    return (
      <Flex p={7} alignItemsCenter justifyContentSpaceBetween>
        <Span variant="h6" style={{ color: 'black', fontWeight: '600' }}>
          {t('Report Data Listing')}
        </Span>
        <Flex alignItemsCenter>
          {renderConfigFilters()}
          <DatePicker
            variant="fareyeV2"
            formatter={yyyyMmDd}
            onDateRangeSelected={(dateRange: IDateRange<string | Date>) =>
              setDateRange(dateRange as IDateRange<Date>)
            }
            value={dateRange as IDateRange<Date>}
          />
          <DownloadExcelButton
            isDisabled={subscriptionId ? false : true}
            fileName={selectedAlertType}
            apiUrl={reportUrl}
          />
        </Flex>
      </Flex>
    )
  }

  const selectedData = React.useMemo(() => {
    return selectedColumns && selectedAlertType ? selectedColumns : []
  }, [selectedAlertType, selectedColumns])
  return (
    <Card bg={'white'} header={reportCardHeader()} style={{ flexGrow: 1 }}>
      {reportListingAlertType === selectedAlertType && (
        <RenderControl state={alertReportData} t={t}>
          <Table
            className="striped responsive"
            columns={getTableColumns(selectedData, { t })}
            data={alertReportData.data}
            totalElements={alertReportData?.data?.length || 0}
            // minRows={0}
            // NoDataComponent={NoData}
            // getTheadProps={(state, index) => ({
            //   style: {
            //     backgroundColor: 'rgba(0,100,145,0.1)'
            //   }
            // })}
            // getTrProps={(state: any, { index }: any) => {
            //   return {
            //     style: {
            //       backgroundColor: `${index % 2 === 1 ? '#e6eff4' : '#FFFFFF'}`
            //     }
            //   }
            // }}
          />
        </RenderControl>
      )}
    </Card>
  )
}
